import { useEffect, useRef, useCallback } from 'react'

const NAVER_SDK_URL = 'https://static.nid.naver.com/js/naveridlogin_js_sdk_2.0.2.js'
const NAVER_CLIENT_ID = import.meta.env.VITE_NAVER_CLIENT_ID

interface NaverLoginButtonProps {
  onToken: (token: string) => void
}

export function NaverLoginButton({ onToken }: NaverLoginButtonProps) {
  const loginBtnRef = useRef<HTMLDivElement>(null)

  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.data?.type === 'NAVER_LOGIN' && event.data?.token) {
      onToken(event.data.token)
    }
  }, [onToken])

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [handleMessage])

  useEffect(() => {
    if (!NAVER_CLIENT_ID) return

    const existingScript = document.querySelector(`script[src="${NAVER_SDK_URL}"]`)

    const initNaverLogin = () => {
      if (!window.naver || !loginBtnRef.current) return
      const naverLogin = new window.naver.LoginWithNaverId({
        clientId: NAVER_CLIENT_ID,
        callbackUrl: `${window.location.origin}/auth/naver`,
        callbackHandle: true,
        isPopup: true,
        loginButton: { color: 'green', type: 3, height: 44 },
      })
      naverLogin.init()
    }

    if (existingScript) {
      initNaverLogin()
      return
    }

    const script = document.createElement('script')
    script.src = NAVER_SDK_URL
    script.onload = () => initNaverLogin()
    document.head.appendChild(script)
  }, [])

  const handleClick = () => {
    const btn = loginBtnRef.current?.querySelector('a, img') as HTMLElement
    btn?.click()
  }

  if (!NAVER_CLIENT_ID) return null

  return (
    <>
      <div ref={loginBtnRef} id="naverIdLogin" className="hidden" />
      <button
        type="button"
        onClick={handleClick}
        className="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-[#03C75A] px-4 py-3 text-white font-medium hover:bg-[#02b351] transition-colors"
      >
        네이버 로그인
      </button>
    </>
  )
}

declare global {
  interface Window {
    naver: {
      LoginWithNaverId: new (config: {
        clientId: string
        callbackUrl: string
        callbackHandle: boolean
        isPopup: boolean
        loginButton: { color: string; type: number; height: number }
      }) => { init: () => void }
    }
  }
}
